// src/pages/ProductDetail.jsx
import { useParams, useLocation } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import FormulaireSejour from './FormulaireSejour';
// import FlightOptions from 'FlightOptions'; // (optionnel)
import PriceCalendar from './PriceCalendar';
import GalerieProduit from './GalerieProduit';

/* ----------------------------- Helpers ----------------------------- */

// harmonise les variations de schéma qu’on a vues dans la liste
function normalizeProduct(p = {}) {
  const bestPrice = p.bestPrice ?? p.BestPrice ?? {};
  const media = Array.isArray(p.media) ? p.media : p.Media || [];
  const froms = p.froms ?? p.Froms ?? [];
  const services = p.services ?? p.info?.services ?? p.info?.[0]?.services ?? [];
  return { ...p, bestPrice, media, froms, services };
}

const normalizeText = (s = '') =>
  s
    .toString()
    .trim()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

// déduplique les services (priorité à l’icône ; sinon au titre)
function dedupeServices(raw = [], productName = '') {
  // const blacklistIconFiles = new Set(['hotel.svg']); // bruit (“Hôtel …”)
  // const productNameNorm = normalizeText(productName);

  const seen = new Set();
  const out = [];
  const norm = (s = '') =>
    s
      .toString()
      .trim()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase();

  for (const s of raw) {
    const iconFile = (s?.icon || '').split('/').pop()?.toLowerCase();
    const titleNorm = norm(s?.title || '');
    const key = iconFile ? `i:${iconFile}` : `t:${titleNorm}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(s);
  }
  return out;
}

/* --------------------------- Composant page --------------------------- */

function ProductDetail() {
  const { id } = useParams();
  const { state } = useLocation();

  // si on vient du catalogue, on a déjà l’objet complet (avec services)
  const [produit, setProduit] = useState(
    state?.product ? normalizeProduct(state.product) : null,
  );
  const [loading, setLoading] = useState(!state?.product);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // --- Chargement produit (fallback si pas de state) ---
  useEffect(() => {
    if (state?.product) return; // on a déjà les données
    const controller = new AbortController();

    async function fetchProduit() {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`/api/products/${id}`, {
          signal: controller.signal,
        });
        setProduit(normalizeProduct(res.data));
      } catch (err) {
        if (axios.isCancel(err) || err.name === 'CanceledError') return;
        console.error('Erreur chargement produit :', err);
        setError('Impossible de charger le produit.');
      } finally {
        setLoading(false);
      }
    }

    fetchProduit();
    return () => controller.abort();
  }, [id, state?.product]);

  // --- Données dérivées ---
  const productCode = produit?._$id || produit?.id || produit?.code?.Value || null;

  const priceHuman = produit?.bestPrice?.bestPrice?.Price?._$Value
    ? `${Math.round(produit.bestPrice.bestPrice.Price._$Value / 100)} €`
    : 'Non disponible';

  const bestFrom = produit?.bestPrice?.bestPrice?._$From ?? produit?.bestPrice?._$From;

  const capitalize = (str) =>
    str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : '';

  const normFrom = (x) =>
    String(x ?? '')
      .toUpperCase()
      .replace(/^_?LT_/, '');

  const froms = produit?.froms ?? [];
  const matchedFrom = froms.find((f) => normFrom(f?.id) === normFrom(bestFrom));

  // Services (dédupliqués)
  const services = useMemo(
    () => dedupeServices(produit?.services || [], produit?.description?.name || ''),
    [produit],
  );

  useEffect(() => {
    if (produit) {
      console.log('DETAIL produit =', produit);
      console.log('DETAIL services =', services);
    }
  }, [produit, services]);

  // --- Submit réservation (appel depuis FormulaireSejour via onSubmit) ---
  async function handleBookingSubmit(formData) {
    const token = localStorage.getItem('token_b2b');
    if (!token) {
      alert('Vous devez être connecté pour réserver (token manquant).');
      return;
    }
    const authHeader = token.startsWith('Bearer ') ? token : `Bearer ${token}`;

    const displayedPriceCents = produit?.bestPrice?.bestPrice?.Price?._$Value ?? null;
    const pricePerPerson =
      displayedPriceCents != null ? Math.round(displayedPriceCents / 100) : null;

    const payload = {
      from: formData.from,
      date: formData.date, // "YYYY-MM-DD"
      durationDays: formData?.duree?.days,
      durationNights: formData?.duree?.nights,
      adults: Number(formData.adults),
      children: Number(formData.children),

      productId: produit?._id || produit?.id || produit?.code?.Value || null,
      productName: produit?.description?.name || null,
      hotelId:
        produit?.hotel?.id ?? produit?.information?.HotelId ?? produit?.info?.hotelId ?? null,
      destination: {
        city: produit?.location?.city?.title || null,
        country: produit?.location?.country?.title || null,
      },
      startsAt: produit?.bestPrice?.startsAt ?? produit?.bestPrice?.startAt ?? null,

      displayedPriceCents,
      pricePerPerson,
      currency: 'EUR',
    };

    // const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

    try {
      setSubmitting(true);
      const res = await axios.post(`/api/bookings`, payload, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: authHeader,
        },
        timeout: 15000,
      });
      alert('Votre demande de réservation a bien été envoyée ✅');
      return res.data;
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      console.error('Erreur lors de la réservation :', status, data || err.message);

      if (status === 401) {
        alert(
          data?.message === 'Token invalide'
            ? 'Session expirée. Veuillez vous reconnecter.'
            : 'Authentification requise.',
        );
      } else if (status === 400) {
        alert(data?.message || 'Données invalides.');
      } else {
        alert('Échec de la réservation. Vérifiez vos infos ou réessayez.');
      }
      throw err;
    } finally {
      setSubmitting(false);
    }
  }

  /* ------------------------------ Render ------------------------------ */

  if (loading) return <p className="text-center p-4">Chargement…</p>;
  if (error) return <p className="text-center text-danger">{error}</p>;
  if (!produit) return <p className="text-center text-danger">Produit introuvable</p>;

  return (
    <div className="container my-4">
      <div className="container-fluid" style={{ marginTop: '8rem' }}>
        <div className="row g-1">
          {/* Titre */}
          <div className="col-12">
            <h2 className="text-secondary p-3">{produit?.description?.name}</h2>
            <h5>
              <i className="fas fa-map-marker-alt" /> {produit?.location?.city?.title} ,{' '}
              {produit?.location?.country?.title}
            </h5>
          </div>

          {/* Grande image */}
          <div className="col-md-7">
            <img
              src={produit?.media?.[0]?.url}
              alt="main"
              className="img-fluid rounded w-100 h-100"
              style={{ objectFit: 'cover', maxHeight: '500px' }}
            />
          </div>

          {/* 2 images empilées */}
          <div className="col-md-4 d-flex flex-column gap-1">
            <img
              src={produit?.media?.[1]?.url}
              alt="second"
              className="img-fluid rounded w-100"
              style={{ objectFit: 'cover', height: '49%' }}
            />
            <img
              src={produit?.media?.[2]?.url}
              alt="third"
              className="img-fluid rounded w-100"
              style={{ objectFit: 'cover', height: '49%', marginTop: '1rem' }}
            />
          </div>

          <GalerieProduit produit={produit} />
        </div>
      </div>

      <div className="row mt-5">
        {/* Infos prix / best from */}
        <div className="col-md-6">
          <div className="d-flex align-items-baseline">
            À partir de{' '}
            <strong>
              <h4 className="text-primary px-1">
                {priceHuman}
                <span>/pers</span>
              </h4>
            </strong>
          </div>
          <div className="d-flex">
            Ville de départ :{' '}
            <h4 style={{ color: '#000064', marginLeft: 5 }}>
              {matchedFrom ? capitalize(matchedFrom.title) : bestFrom || '—'}
            </h4>
          </div>
          <div className="d-flex">
            Le{' '}
            <h4 style={{ color: '#000064', marginLeft: 5 }}>
              {produit?.bestPrice?.startsAt || produit?.bestPrice?.startAt || 'Non disponible'}
            </h4>
          </div>
          <div className="d-flex">
            Pour{' '}
            <h4 style={{ color: '#000064', marginLeft: 5 }}>
              {produit?.information?.Night ?? produit?.info?.Night ?? '—'} nuitées
            </h4>
          </div>

          {/* Services */}
          {(services?.length ?? 0) > 0 && (
            <div className="mt-5">
              <h2 className="h5 mb-3" style={{ color: '#f56371' }}>
                Services
              </h2>
              <div className="d-flex flex-wrap gap-3">
                {services.map((s, idx) => (
                  <div
                    key={s.id || s._id || s.title || idx}
                    className="d-flex align-items-center gap-2 border rounded px-2 py-1 col-3 justify-content-center m-2"
                  >
                    {s.icon ? (
                      <img
                        src={s.icon.startsWith('/') ? s.icon : s.icon}
                        alt=""
                        width="28"
                        height="28"
                        style={{ objectFit: 'contain' }}
                        loading="lazy"
                      />
                    ) : null}
                    <span className="small pl-1">{s.title}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Formulaire réservation */}
        <div className="col-md-6">
          <FormulaireSejour
            produit={produit}
            onSubmit={handleBookingSubmit}
            submitting={submitting}
          />
        </div>
      </div>

      {/* Description structurée */}
      {produit?.description?.body?.length > 0 && (
        <div className="mt-5">
          <h2 className="h5 mb-3" style={{ color: '#f56371' }}>
            Détails du produit
          </h2>
          {produit.description.body.map((section, index) => (
            <div key={index} className="bg-light p-3 rounded mb-3">
              <h3 className="h6 mb-2">{section.title}</h3>
              {section.paragraph?.Text ? (
                <div dangerouslySetInnerHTML={{ __html: section.paragraph.Text }} />
              ) : (
                <p className="text-muted mb-0">Pas de contenu.</p>
              )}
            </div>
          ))}
        </div>
      )}

      <>
        <PriceCalendar productCode={productCode} />
        {/* <h5 className="mt-4">Disponibilités de vols</h5> */}
        {/* <FlightOptions fromCode={bestFrom} toCode={toCode} date={selectedDate} /> */}
      </>
    </div>
  );
}

export default ProductDetail;
